import React, { useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import BackgroundImage from '@/operator/components/general/BackgroundImage';
import { StyledTwoScreensWrapper } from '@/operator/components/modules/AuthPage/Styles';
import { MessageWithTip } from '@/operator/components/modules/AuthPage/setup/MessageWithTip';
import { StyledWidthWrapper } from '@/operator/components/modules/AuthPage/setup/Styles';
import { StyledDraggableContainer } from '@/operator/components/modules/AuthPage/setup/progress/Styles';
import {
    removeEmptyStringProperties,
    updateImageSize,
} from '@/operator/utils/utils';
import type { ApolloError } from '@apollo/client';
import useAuth from 'Modules/firebase/use-auth';
import { CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useOrgNavigate } from "app/routes/useOrgNavigate";
import type { ProvisionTenantInputOwnerData } from '@/operator/server/graphql/types/ProvisionTenantInputOwnerData';

import { useAppContext } from '../context/AppContext';
import useOnHandlePageReload from '../hooks/useOnHandlePageReload';
import useOnLeavingPageConfirmation from '../hooks/useOnLeavingPageConfirmation';
import { useNotificationContext } from 'app/contexts/NotificationContext';
import image from '@/operator/public/assets/images/background/team-building.svg';
import { ROUTES } from '@/routes/constants';
import { useSignupMutation } from 'generated/graphql';
import useAuthLogout from 'hooks/useAuthLogout';

const tenantURLDomain = process.env.NEXT_PUBLIC_TENANT_URL_DOMAIN;

const ProgressPage: React.FC = () => {
    const { user, getAccessTokenSilently } = useAuth();
    const logout = useAuthLogout();
    const navigate = useOrgNavigate();

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { t } = useTranslation();
    const { givenName, surName, preferredName, pronouns, avatar } =
        useAppContext();

    const avatarContent = avatar || updateImageSize(user?.picture || '');

    const { showNotification } = useNotificationContext();

    useOnHandlePageReload(() => navigate(ROUTES.AUTH_SETUP_BUILD));

    const { businessName, vanityURL } = useAppContext();

    // new flag for showing the error once only
    const [shownError, setShownError] = useState<boolean>(false);
    // reference for timer clean up
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // we now send a variable flag when receiving an error
    // do not ask for the leaving the page popup
    useOnLeavingPageConfirmation(
        t('General.OnLeavingPageConfirmation.GeneralMessage'),
        { disableBeforeUnloadListener: shownError },
    );

    const errorHandler = (error: ApolloError) => {
        if (!shownError) {
            const errorMessage = t('Notifications.provisionTenantError', {
                orgName: businessName,
            });

            Sentry.captureException(error);

            // show error notification
            showNotification({
                message: errorMessage,
                severity: 'error',
            });

            // trigger flags
            setShownError(true);

            //delay for the redirect
            timerRef.current = setTimeout(logout, 1500);
        }
    };

    const [mutate, signup] = useSignupMutation({
        onError: errorHandler,
    });

    const canProvisionTenant = businessName && vanityURL && user;

    const startTenantProvisioning = async () => {
        if (!canProvisionTenant) {
            return navigate(ROUTES.AUTH_SETUP_BUILD);
        }

        const ownerData = {
            email: user.email,
            authIdentity: user.provider,
            ...(removeEmptyStringProperties({
                givenName: givenName,
                preferredName: preferredName,
                pronouns: pronouns,
                avatarImage: avatarContent,
                surname: surName,
            }) as ProvisionTenantInputOwnerData),
        };

        mutate({
            variables: {
                input: {
                    organizationName: vanityURL,
                    subDomain: vanityURL,
                    displayName: businessName,
                    ownerData,
                },
            },
        });
    };

    useEffect(() => {
        if (!signup.loading && signup.data) {
            navigate(ROUTES.AUTH_SETUP_DONE);
        }
    }, [signup.loading, signup.data]);

    useEffect(() => {
        if (user) {
            startTenantProvisioning();
            return () => clearTimeout(timerRef.current as NodeJS.Timeout);
        }
    }, [user]);

    return (
        <StyledTwoScreensWrapper>
            <MessageWithTip
                Icon={CircularProgress}
                headline={t('Auth.Setup.Progress.Content.Header')}
                description={t('Auth.Setup.Progress.Content.Subheader')}
                tip={t('Auth.Setup.Progress.Content.Tip.Message') as string}
                tipHeadline={
                    t('Auth.Setup.Progress.Content.Tip.Headline') as string
                }
                footerSlot={
                    <StyledWidthWrapper
                        style={{
                            marginTop: '14px',
                            marginLeft: '4px',
                        }}
                    >
                        <Link
                            to={`https://${vanityURL}.${tenantURLDomain}`}
                            style={{ textDecoration: 'none', color: 'black' }}
                        >
                            <StyledDraggableContainer>
                                <Typography
                                    sx={{
                                        display: 'block',
                                        fontSize: '0.625rem',
                                        fontFamily: 'Inter-Regular',
                                        lineHeight: '1.625rem',
                                        letterSpacing: 0,
                                        marginBottom: '20px',
                                    }}
                                    style={{ marginBottom: 0 }}
                                >
                                    {t(
                                        'Auth.Setup.Progress.Content.BookMark.SubHeader',
                                    )}
                                </Typography>
                            </StyledDraggableContainer>
                        </Link>
                    </StyledWidthWrapper>
                }
            />
            <BackgroundImage src={image} />
        </StyledTwoScreensWrapper>
    );
};

export default ProgressPage;
